/**
 * Tiroir de navigation mobile (sous 1024 px).
 *
 * Présentation E (module `ui_template_e`, ADR 0068) : fenêtre Radix (piège du
 * focus, Échap, retour du focus au bouton « Menu »), en-tête chrome bleu nuit,
 * entrées de 48 px. Sans le module : tiroir d'origine, inchangé. Les menus et
 * les entrées sont les mêmes dans les deux cas.
 */
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { HelpCircle, Menu, X } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { APP_LOGO_TEXT, activeNavHref, visibleNavGroups } from "@/config/navigation";
import { useAuthStore } from "@/features/auth/store";

import { Button } from "@/components/ui/button";
import { useIsUiTemplateE } from "@/lib/hooks/use-ui-template";
import { cn } from "@/lib/utils";

import { AmountsVisibilityToggle } from "./AmountsVisibilityToggle";
import { GlobalSearch } from "./GlobalSearch";
import { ThemeToggle } from "./ThemeToggle";

export function MobileNav() {
  const [open, setOpen] = useState(false);
  const uiE = useIsUiTemplateE();

  const trigger = (
    <Button variant="ghost" size="icon" onClick={() => setOpen(true)} aria-label="Menu" data-help="nav.menu">
      <Menu className="h-5 w-5" />
    </Button>
  );

  if (uiE) {
    return (
      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-chrome/55 lg:hidden" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="fixed inset-y-0 left-0 z-50 flex w-80 max-w-[86vw] flex-col border-r border-border bg-surface lg:hidden"
          >
            <div className="flex min-h-14 items-center justify-between bg-chrome py-1.5 pl-4 pr-2 text-chrome-foreground">
              <DialogPrimitive.Title className="font-title text-xl font-bold tracking-[0.08em]">
                {APP_LOGO_TEXT}
                <span className="sr-only"> — navigation</span>
              </DialogPrimitive.Title>
              <DialogPrimitive.Close asChild>
                <button
                  type="button"
                  aria-label="Fermer"
                  className="grid h-11 w-11 place-items-center text-chrome-muted hover:bg-chrome-line hover:text-chrome-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan"
                >
                  <X className="h-5 w-5" />
                </button>
              </DialogPrimitive.Close>
            </div>
            <div className="flex-1 overflow-y-auto pb-6 pt-3">
              <DrawerBody tall onNavigate={() => setOpen(false)} />
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  }

  return (
    <>
      {trigger}

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
          <div className="fixed inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-80 max-w-[85vw] overflow-y-auto bg-surface p-6 shadow-lg">
            <div className="mb-6 flex items-center justify-between">
              <span className="text-lg font-semibold text-muted-foreground">Navigation</span>
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label="Fermer">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <DrawerBody onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Contenu du tiroir : outils (sous sm) puis menus. `tall` = présentation E
 * (entrées de 48 px, marges latérales portées par les entrées).
 */
function DrawerBody({ tall = false, onNavigate }: { tall?: boolean; onNavigate: () => void }) {
  const me = useAuthStore((s) => s.me);
  const { pathname, search } = useLocation();

  // Mêmes menus et même entrée active que la barre (règles pures de
  // `config/navigation`, ADR 0061) : un module éteint ou masqué par la
  // configuration d'affichage disparaît aussi du tiroir.
  const visibleGroups = visibleNavGroups(me);
  const activeHref = activeNavHref(visibleGroups, pathname, search);

  const linkClass = (active: boolean) =>
    cn(
      "flex items-center gap-3 text-sm font-medium transition-colors",
      tall ? "min-h-12 border-l-[3px] px-4" : "border-l-2 px-3 py-2",
      active
        ? "border-primary-text bg-primary/10 text-primary-text"
        : "border-transparent text-muted-foreground hover:bg-background",
      tall && active && "font-semibold",
    );
  const groupTitleClass = cn(
    "text-xs font-semibold uppercase tracking-wider text-muted",
    tall ? "mb-1 px-4" : "mb-2",
  );

  return (
    <>
      {/* Recherche + thème — masqués dans la navbar sous sm, accessibles ici. */}
      <div
        className={cn(
          "flex items-center gap-2 border-b border-border sm:hidden",
          tall ? "mb-3 px-4 pb-3" : "mb-6 pb-6",
        )}
      >
        <GlobalSearch
          triggerClassName={cn(
            "inline-flex flex-1 items-center gap-2 border border-border bg-background px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
            tall && "min-h-11",
          )}
          triggerLabel="Rechercher…"
          onNavigate={onNavigate}
        />
        <ThemeToggle className="border border-border text-muted-foreground hover:bg-background hover:text-foreground" />
        <AmountsVisibilityToggle className="border border-border text-muted-foreground hover:bg-background hover:text-foreground" />
      </div>

      <nav className={tall ? "space-y-4" : "space-y-6"}>
        {visibleGroups.map((group) => (
          <div key={group.label}>
            <div className={groupTitleClass}>{group.label}</div>
            {group.sections.map((section, index) => (
              <div
                key={section.label ?? `top-${index}`}
                role={section.label ? "group" : undefined}
                aria-label={section.label}
                className={cn(section.label && index > 0 && "mt-3")}
              >
                {section.label && (
                  <div
                    className={cn(
                      "mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground",
                      tall ? "px-4" : "px-3",
                    )}
                  >
                    {section.label}
                  </div>
                )}
                <ul className={tall ? undefined : "space-y-1"}>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = item.href === activeHref;
                    return (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          onClick={onNavigate}
                          aria-current={active ? "page" : undefined}
                          className={linkClass(active)}
                        >
                          {Icon && <Icon className="h-4 w-4" />}
                          {item.label}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        ))}
        <div>
          <div className={groupTitleClass}>Aide</div>
          <Link to="/aide" onClick={onNavigate} className={linkClass(pathname.startsWith("/aide"))}>
            <HelpCircle className="h-4 w-4" /> Guide et visites guidées
          </Link>
        </div>
      </nav>
    </>
  );
}
